import React, {createContext, useContext, useEffect, useCallback} from 'react';
import {useAnonymousAuth} from '../AnonymousAuthProvider/AnonymousAuthProvider';
import {useFirebaseRemoteConfig} from '../FirebaseRemoteConfigProvider/FirebaseRemoteConfigProvider';
import usePaywal from '@src/Hooks/usePaywal';
import {
  pageRequiresAuth,
  elementRequiresAuth,
} from '@src/Services/AccessControl/accessRules';
import {contentAccessService} from '@src/Services/ContentAccess/ContentAccessService';
import {AppRoutesEnum} from '@src/Constant/routes';
import {
  useRouteChange,
  UseRouteChangeCallbackParams,
} from '@src/Hooks/useRouteChange';
import {useI18next} from 'gatsby-plugin-react-i18next';
import {ensureCleanLocalePrefix} from '@src/utils/url/ensureCleanLocalePrefix';
// updateGraphQLAuthState n'est plus nécessaire - Apollo Link utilise directement le store

interface AuthGuardContextType {
  checkPageAccess: (route: string) => boolean;
  checkElementAccess: (element: HTMLElement) => boolean;
  showAuthModal: (reason?: string) => void;
}

const AuthGuardContext = createContext<AuthGuardContextType>({
  checkPageAccess: () => true,
  checkElementAccess: () => true,
  showAuthModal: () => {},
});

export const useAuthGuard = () => {
  const context = useContext(AuthGuardContext);
  if (!context) {
    throw new Error('useAuthGuard must be used within AuthGuardProvider');
  }
  return context;
};

interface AuthGuardProviderProps {
  children: React.ReactNode;
}

/**
 * AuthGuardProvider - Provider unifié pour la sécurisation de l'application
 *
 * Fusion de GlobalAuthGuardProvider + ContentAccessProvider
 *
 * Responsabilités:
 * 1. Configuration du ContentAccessService (injection des dépendances)
 * 2. Interception des clics et navigation (protection des routes)
 * 3. Gestion des événements d'authentification
 * 4. Synchronisation des états d'auth
 */
const AuthGuardProvider: React.FC<AuthGuardProviderProps> = ({children}) => {
  const {isAnonymous, user, isInitialized} = useAnonymousAuth();
  const {anonymousAuthEnabled} = useFirebaseRemoteConfig();
  const paywallHook = usePaywal();
  const {language} = useI18next();

  // === 1. CONFIGURATION DU SERVICE (ex-ContentAccessProvider) ===

  // Injection des méthodes paywall dans le service (une seule fois)
  useEffect(() => {
    console.log('🔧 [AuthGuard] Injecting paywall methods');

    contentAccessService.setPaywallMethods({
      canSeeContent: (accessType?: string) => {
        return paywallHook.canSeeContent(accessType as any);
      },
      canPreview: paywallHook.canPreview,
    });

    console.log('✅ [AuthGuard] Paywall methods injected');
  }, []); // Une seule fois au montage

  // Injection de la méthode d'authentification anonyme
  useEffect(() => {
    if (anonymousAuthEnabled) {
      console.log('🔧 [AuthGuard] Injecting anonymous auth method');

      const anonymousAuthMethod = async (): Promise<boolean> => {
        try {
          if (!anonymousAuthEnabled) {
            console.log(
              '🚫 [AuthGuard] Anonymous auth disabled via remote config',
            );
            return false;
          }

          if (user && isAnonymous) {
            console.log('✅ [AuthGuard] Anonymous user already exists');
            return true;
          }

          console.log(
            '🔍 [AuthGuard] Anonymous auth will be handled by AnonymousAuthProvider',
          );
          return false;
        } catch (error) {
          console.error('❌ [AuthGuard] Anonymous auth error:', error);
          return false;
        }
      };

      contentAccessService.setAnonymousAuthMethod(anonymousAuthMethod);
    }
  }, [anonymousAuthEnabled, user, isAnonymous]);

  // Synchronisation de l'état d'authentification
  useEffect(() => {
    const isLoggedIn = !!user && !isAnonymous;

    console.log('🔄 [AuthGuard] Updating auth state', {
      isLoggedIn,
      isAnonymous,
      hasUser: !!user,
    });

    // Synchroniser avec le ContentAccessService
    contentAccessService.updateAuthState(isLoggedIn, isAnonymous);

    // Plus besoin de synchroniser avec GraphQL - Apollo Link utilise directement le store Redux
  }, [user, isAnonymous, isInitialized]);

  useEffect(
    function recheckRouteAccessOnAuthChange() {
      if (!isInitialized) return;
      const currentPath = window.location.pathname;
      handleRouteChange({
        currentPath,
        previousPath: '',
        currentSearch: '',
        previousSearch: '',
      });
    },
    [isInitialized, user, isAnonymous],
  );

  // === 2. PROTECTION DES ROUTES ET ÉLÉMENTS (ex-GlobalAuthGuardProvider) ===

  const showAuthModal = (reason?: string, redirectTo = AppRoutesEnum.HOME) => {
    // 🔧 CORRECTION : Déclencher la modal même sans utilisateur anonyme
    // Cas 1: Utilisateur anonyme (anonymous_auth = true)
    // Cas 2: Aucun utilisateur mais accès public (anonymous_auth = false)
    const isLoggedIn = !!user && !isAnonymous;
    const shouldShowModal = (isAnonymous && user) || (!user && !isLoggedIn);

    if (!shouldShowModal) return;

    console.log(
      '🔒 [AuthGuard] Déclenchement auth modal:',
      reason || 'Route/Action protégée',
      {isAnonymous, hasUser: !!user, isLoggedIn},
    );

    // Utiliser le ContentAccessService configuré
    contentAccessService.checkContentAccess(
      {
        contentType: 'article',
        contentId: `protected-route-${window.location.pathname}`,
        redirectTo: redirectTo || window.location.pathname,
        metadata: {
          title: reason || 'Route protégée',
        },
      },
      {
        forceCheck: true,
      },
    );
  };

  const checkPageAccess = (route: string): boolean => {
    if (!isInitialized) return true;

    const accessRule = pageRequiresAuth(route);
    const isLoggedIn = !!user && !isAnonymous;

    // 🔧 CORRECTION : Bloquer l'accès si authentification requise ET pas connecté
    if (accessRule && (!isLoggedIn || isAnonymous)) {
      console.log('🚫 [AuthGuard] Accès à la page bloqué:', route, {
        isLoggedIn,
        isAnonymous,
        hasUser: !!user,
      });
      showAuthModal(
        `Accès à la page: ${accessRule.description || route}`,
        route as AppRoutesEnum,
      );
      return false;
    }

    return true;
  };

  const checkElementAccess = (element: HTMLElement): boolean => {
    if (!isInitialized) return true;

    const accessRule = elementRequiresAuth(element);
    const isLoggedIn = !!user && !isAnonymous;

    // 🔧 CORRECTION : Bloquer l'interaction si authentification requise ET pas connecté
    if (accessRule && (!isLoggedIn || isAnonymous)) {
      console.log(
        '🚫 [AuthGuard] Interaction bloquée:',
        accessRule.description,
        {isLoggedIn, isAnonymous, hasUser: !!user},
      );
      showAuthModal(
        `Action: ${accessRule.description || 'Interaction utilisateur'}`,
      );
      return false;
    }

    return true;
  };

  // === 3. INTERCEPTION GLOBALE ===

  // Callback pour vérifier l'accès aux routes
  const handleRouteChange = useCallback(
    ({
      currentPath,
      previousPath,
      currentSearch,
      previousSearch,
    }: UseRouteChangeCallbackParams) => {
      if (!isInitialized) return;

      console.log('🧭 [AuthGuard] Route change detected:', {
        currentPath,
        previousPath,
        isAnonymous,
        isInitialized,
      });

      const accessRule = pageRequiresAuth(currentPath);
      const isLoggedIn = !!user && !isAnonymous;

      // Construire l'URL de redirection complète selon le choix de path
      let fullRedirectUrl: string;
      if (previousPath) {
        fullRedirectUrl = previousPath + (previousSearch || '');
      } else {
        fullRedirectUrl = currentPath + (currentSearch || '');
      }

      const cleanRedirectUrl = ensureCleanLocalePrefix(
        fullRedirectUrl,
      ) as AppRoutesEnum;

      // 🔧 CORRECTION : Déclencher modal si authentification requise ET pas connecté
      if (accessRule && (!isLoggedIn || isAnonymous)) {
        console.log('🔒 [AuthGuard] Protected route accessed:', currentPath, {
          isLoggedIn,
          isAnonymous,
          hasUser: !!user,
        });
        showAuthModal(
          `Accès à la page: ${accessRule.description || currentPath}`,
          cleanRedirectUrl,
        );
      }
    },
    [isInitialized, isAnonymous, user, showAuthModal],
  );

  // Surveiller les changements de route avec notre hook personnalisé
  useRouteChange(handleRouteChange, true); // immediate: true pour vérifier au montage

  // Intercepter les clics globalement
  useEffect(() => {
    if (!isInitialized) return;

    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target) return;

      // Vérifier l'élément et ses parents
      let currentElement: HTMLElement | null = target;
      while (currentElement) {
        if (!checkElementAccess(currentElement)) {
          event.preventDefault();
          event.stopPropagation();
          return false;
        }
        currentElement = currentElement.parentElement;
      }
    };

    document.addEventListener('click', handleGlobalClick, true);
    return () => document.removeEventListener('click', handleGlobalClick, true);
  }, [isInitialized, isAnonymous, user]);

  // Intercepter les liens de navigation
  useEffect(() => {
    if (!isInitialized) return;

    const handleLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLAnchorElement;

      if (target.tagName === 'A' && target.href) {
        const url = new URL(target.href);
        const path = url.pathname;
        const accessRule = pageRequiresAuth(path);
        const isLoggedIn = !!user && !isAnonymous;

        // 🔧 CORRECTION : Intercepter si authentification requise ET pas connecté
        if (accessRule && (!isLoggedIn || isAnonymous)) {
          console.log(
            '🔒 [AuthGuard] Lien vers page protégée intercepté:',
            path,
            {isLoggedIn, isAnonymous, hasUser: !!user},
          );
          event.preventDefault();
          showAuthModal(`Navigation vers: ${accessRule.description || path}`);
          return false;
        }
      }
    };

    document.addEventListener('click', handleLinkClick, true);
    return () => document.removeEventListener('click', handleLinkClick, true);
  }, [isInitialized, isAnonymous, user]);

  // Écouter les événements d'auth requis depuis les appels d'API
  useEffect(() => {
    if (!isInitialized) return;

    const handleAuthRequired = (event: CustomEvent) => {
      const isLoggedIn = !!user && !isAnonymous;

      // 🔧 CORRECTION : Déclencher modal si pas connecté (anonyme ou pas d'utilisateur)
      if (isLoggedIn) return;

      const {actionId, endpoint, method, reason} = event.detail;
      console.log('🔒 [AuthGuard] Auth required from API call:', {
        actionId,
        endpoint,
        method,
        isLoggedIn,
        isAnonymous,
        hasUser: !!user,
      });

      showAuthModal(
        reason ||
          `API call requires authentication: ${actionId || 'unknown action'}`,
      );
    };

    window.addEventListener(
      'authRequired',
      handleAuthRequired as EventListener,
    );
    return () =>
      window.removeEventListener(
        'authRequired',
        handleAuthRequired as EventListener,
      );
  }, [isInitialized, isAnonymous, user, showAuthModal]);

  // === 4. INITIALISATION ===

  useEffect(() => {
    console.log('✅ [AuthGuard] Provider initialized and configured');
    return () => console.log('🔧 [AuthGuard] Provider unmounting');
  }, []);

  return (
    <AuthGuardContext.Provider
      value={{
        checkPageAccess,
        checkElementAccess,
        showAuthModal,
      }}>
      {children}
    </AuthGuardContext.Provider>
  );
};

export default AuthGuardProvider;
